/**
 * Sound manager — notification tones (SMS) and in-app call ringtones.
 *
 * Preferences are stored per user on the server (/push/preferences and
 * /push/sound-defaults) and mirrored into localStorage so playback works
 * instantly without a round-trip. Background/system push notifications keep
 * the device's system sound — browsers do not allow custom web-push sounds.
 */

let audioContext = null;
let lastPlayedAt = 0;
let previewAudio = null;
let ringerAudio = null;

const SOUND_PREF_KEY = 'fieldz_notification_sound';
const PREFS_KEY = 'fieldz_sound_prefs_v1';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const SMS_TONES = [
  { id: 'default', name: 'Default Chime', file: null },
  { id: 'chime', name: 'Chime', file: '/sounds/sms-chime.wav' },
  { id: 'bell', name: 'Bell', file: '/sounds/sms-bell.wav' },
  { id: 'pop', name: 'Pop', file: '/sounds/sms-pop.wav' },
  { id: 'alert', name: 'Alert', file: '/sounds/sms-alert.wav' },
  { id: 'soft', name: 'Soft Notification', file: '/sounds/sms-soft.wav' },
  { id: 'double', name: 'Double Chime', file: '/sounds/sms-double.wav' },
  { id: 'custom', name: 'My Custom Sound', file: null },
  { id: 'silent', name: 'None / Silent', file: null },
];

export const RING_TONES = [
  { id: 'default', name: 'Classic Ring (Default)', file: '/sounds/ring-classic.wav' },
  { id: 'digital', name: 'Digital Ring', file: '/sounds/ring-digital.wav' },
  { id: 'office', name: 'Office Phone', file: '/sounds/ring-office.wav' },
  { id: 'modern', name: 'Modern Ring', file: '/sounds/ring-modern.wav' },
  { id: 'soft', name: 'Soft Ring', file: '/sounds/ring-soft.wav' },
  { id: 'loud', name: 'Loud Ring', file: '/sounds/ring-loud.wav' },
  { id: 'custom', name: 'My Custom Ringtone', file: null },
  { id: 'silent', name: 'None / Silent', file: null },
];

/* ── preference cache ── */

export function getSoundPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch {
    return {};
  }
}

export function updateSoundPrefs(partial) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...getSoundPrefs(), ...partial }));
  } catch { /* storage full/blocked */ }
}

/** Pull the user's saved sound preferences + account defaults from the server. */
export async function syncSoundPrefs(apiBase) {
  const token = localStorage.getItem('token') || localStorage.getItem('tech_token');
  if (!token) return;
  const headers = { Authorization: `Bearer ${token}` };
  try {
    const [p, d] = await Promise.all([
      fetch(`${apiBase}/push/preferences`, { headers }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`${apiBase}/push/sound-defaults`, { headers }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]);
    if (p) {
      updateSoundPrefs({
        sms_enabled: p.notification_sound !== false,
        sms_tone: p.sms_tone || 'default',
        call_ringtone: p.call_ringtone || 'default',
        call_ring_enabled: p.call_ring_enabled !== false,
        ring_volume: typeof p.ring_volume === 'number' ? p.ring_volume : 1,
        location_ringtones_enabled: p.location_ringtones_enabled !== false,
        custom_sms_url: p.custom_sms_url || null,
        custom_ring_url: p.custom_ring_url || null,
      });
      setSoundEnabled(p.notification_sound !== false);
    }
    if (d) {
      updateSoundPrefs({
        account_default_sms_tone: d.default_sms_tone || 'default',
        account_default_ringtone: d.default_call_ringtone || 'default',
        location_ringtones: d.location_ringtones || {},
      });
    }
  } catch { /* offline — keep cached prefs */ }
}

/* ── legacy on/off API (kept for compatibility) ── */

export function isSoundEnabled() {
  return localStorage.getItem(SOUND_PREF_KEY) !== 'off';
}

export function setSoundEnabled(on) {
  localStorage.setItem(SOUND_PREF_KEY, on ? 'on' : 'off');
  updateSoundPrefs({ sms_enabled: !!on });
}

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith('http') || u.startsWith('/sounds/')) return u;
  return `${BACKEND}${u}`;
}

function toneUrl(kind, id, prefs) {
  if (id === 'custom') {
    return resolveUrl(kind === 'sms' ? prefs.custom_sms_url : prefs.custom_ring_url);
  }
  const list = kind === 'sms' ? SMS_TONES : RING_TONES;
  return list.find((t) => t.id === id)?.file || null;
}

function clampVol(v) {
  const n = typeof v === 'number' ? v : 1;
  return Math.min(1, Math.max(0, n));
}

/** The original synthesized two-tone chime (the "Default Chime"). */
function synthChime() {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') ctx.resume();
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(880, ctx.currentTime);
  oscillator.frequency.setValueAtTime(1174.66, ctx.currentTime + 0.1);
  gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + 0.4);
}

/* ── SMS tone ── */

/**
 * Play the user's selected incoming-SMS tone.
 * - Respects the enabled toggle (unless { force: true })
 * - Throttled so rapid-fire messages never overlap
 * - { toneId } overrides the saved tone (used by Test buttons)
 */
export function playSmsTone(opts = {}) {
  try {
    const prefs = getSoundPrefs();
    const enabled = prefs.sms_enabled !== false && isSoundEnabled();
    if (!opts.force && !enabled) return;
    const now = Date.now();
    if (!opts.force && now - lastPlayedAt < 1500) return;
    lastPlayedAt = now;

    let tone = opts.toneId || prefs.sms_tone;
    if (!tone) tone = prefs.account_default_sms_tone || 'default';
    if (tone === 'silent') return;
    const url = toneUrl('sms', tone, prefs);
    if (!url) {
      synthChime();
      return;
    }
    const a = new Audio(url);
    a.play().catch(() => {});
  } catch { /* autoplay policy or no audio support */ }
}

/** Back-compat: general notification chime (non-SMS alerts). */
export function playNotificationSound(opts = {}) {
  playSmsTone(opts);
}

/* ── previews (Settings UI) ── */

/**
 * Play a one-shot sample of a tone. Only one preview plays at a time.
 * Returns the Audio element (or null) so callers can watch 'ended'.
 */
export function previewTone(kind, id, opts = {}) {
  stopPreview();
  try {
    if (id === 'silent') return null;
    const prefs = getSoundPrefs();
    if (kind === 'sms' && (id === 'default' || !toneUrl('sms', id, prefs))) {
      if (id === 'default') {
        synthChime();
        return null;
      }
      return null;
    }
    const url = toneUrl(kind, id, prefs);
    if (!url) return null;
    previewAudio = new Audio(url);
    if (opts.volume != null) previewAudio.volume = clampVol(opts.volume);
    previewAudio.play().catch(() => {});
    return previewAudio;
  } catch {
    return null;
  }
}

export function stopPreview() {
  if (previewAudio) {
    try { previewAudio.pause(); } catch { /* ignore */ }
    previewAudio = null;
  }
}

/* ── in-app call ringer ── */

/**
 * Resolve which ringtone should play for an incoming in-app call.
 * Priority: location ringtone (when configured + user allows it) →
 * user's own choice → account default → system default (classic).
 */
export function resolveRingTone(locationId) {
  const p = getSoundPrefs();
  if (p.call_ring_enabled === false) return 'silent';
  if (p.call_ringtone === 'silent') return 'silent';
  const locMap = p.location_ringtones || {};
  if (p.location_ringtones_enabled !== false && locationId && locMap[locationId]) {
    return locMap[locationId];
  }
  if (p.call_ringtone && p.call_ringtone !== 'default') return p.call_ringtone;
  if (!p.call_ringtone && p.account_default_ringtone && p.account_default_ringtone !== 'default') {
    return p.account_default_ringtone;
  }
  return 'default';
}

/**
 * Start looping the resolved ringtone for an incoming call.
 * Always call stopRinger() when the call is answered/declined/canceled/ended.
 */
export function startRinger({ locationId } = {}) {
  stopRinger();
  try {
    const id = resolveRingTone(locationId);
    if (id === 'silent') return id;
    const prefs = getSoundPrefs();
    const url = toneUrl('ring', id, prefs) || RING_TONES[0].file;
    ringerAudio = new Audio(url);
    ringerAudio.loop = true;
    ringerAudio.volume = clampVol(prefs.ring_volume);
    ringerAudio.play().catch(() => {});
    return id;
  } catch {
    return null;
  }
}

export function stopRinger() {
  if (ringerAudio) {
    try {
      ringerAudio.pause();
      ringerAudio.currentTime = 0;
    } catch { /* ignore */ }
    ringerAudio = null;
  }
}

/* ── diagnostics ── */

/** True when the browser's autoplay policy is currently blocking audio. */
export function isSoundBlocked() {
  try {
    return getAudioContext().state === 'suspended';
  } catch {
    return true;
  }
}

/** Human-readable reason sound can't play right now, or null if it can. */
export function getAudioBlockReason() {
  try {
    if (!window.AudioContext && !window.webkitAudioContext) {
      return 'This browser does not support audio playback';
    }
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      return 'Your browser blocks sound until you interact with the page — tap or click anywhere, then try again';
    }
    return null;
  } catch {
    return 'Audio is unavailable in this browser';
  }
}

/**
 * Set up a listener for push notifications received while the app is open.
 * The service worker posts a message to the client when a push arrives.
 * @param {function} onNotification - Callback with { title, body, data, url }
 * @returns {function} cleanup function
 */
export function listenForPushMessages(onNotification) {
  const handler = (event) => {
    if (event.data?.type === 'PUSH_RECEIVED') {
      onNotification(event.data.notification);
    }
    if (event.data?.type === 'NAVIGATE') {
      window.location.href = event.data.url;
    }
  };

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handler);
  }

  return () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', handler);
    }
  };
}
